/**
 * History/Review plugin — Studio's proposals/review/comments CLIENT surface,
 * mounted purely through the workbench seam set (the F4 thesis test):
 *   - navSections  → the proposal list (a rail section)
 *   - panelKinds   → the diff/review panel as a custom tab
 *   - commands     → "New proposal" / "Propose changes"
 *   - contextMenu  → "Comment here" on a board node
 *
 * It owns no data feed of its own (no `providers`): the proposals data is
 * app-global react-query, and cross-seam UI intents flow through the module-level
 * `historyStore`. The server routes stay core — consumed as-is via `proposalsApi`
 * (not coupled to the backend feature-module relocation, B1).
 */
import {
  WORKBENCH_PLUGIN_API_VERSION,
  type WorkbenchPlugin,
  type JsonValue,
  type OpenTabSpec,
} from "@archlang/app-shell/workbench";
import { GitPullRequest } from "lucide-react";
import { ProposalsNavPanel } from "./ProposalsNavPanel";
import { ReviewTabRenderer } from "./ReviewTabRenderer";
import { parseReviewState } from "./reviewState";
import { requestNewProposal, setProposeOpen, startNodeComment } from "./historyStore";

export function historyPlugin(): WorkbenchPlugin {
  return {
    id: "history",
    title: "Proposals & review",
    apiVersion: WORKBENCH_PLUGIN_API_VERSION,
    contributes: {
      navSections: [
        {
          id: "history:proposals",
          title: "Proposals",
          // A proposal IS a merge request (draft → proposed → accepted), so the
          // rail says so. Without an explicit icon the host falls back to the
          // generic `Puzzle` — correct for an unknown third-party plugin, wrong
          // for a first-party surface with a domain of its own. Sized to the
          // mode-tab convention (h-5 w-5 / strokeWidth 1.8) so it sits level
          // with Board/Types/Process in the same strip.
          icon: <GitPullRequest className="h-5 w-5" strokeWidth={1.8} aria-hidden />,
          order: 10,
          when: (ctx) => ctx.activeRepo != null,
          Panel: ProposalsNavPanel,
        },
      ],
      panelKinds: [
        {
          kind: "history:review",
          title: (spec: OpenTabSpec) =>
            parseReviewState((spec.state ?? null) as JsonValue)?.title?.trim() || "Review",
          idFor: (spec: OpenTabSpec) => {
            const s = parseReviewState((spec.state ?? null) as JsonValue);
            return s ? s.changesetId : "";
          },
          serialize: (state: JsonValue) => state,
          revive: (raw: JsonValue) => (parseReviewState(raw) ? raw : null),
          Renderer: ReviewTabRenderer,
        },
      ],
      commands: [
        {
          id: "history.new-proposal",
          title: "New proposal",
          group: "Proposals",
          keywords: ["changeset", "propose", "draft"],
          when: (ctx) => ctx.activeRepo != null,
          run: () => requestNewProposal(),
        },
        {
          id: "history.propose",
          title: "Propose changes",
          group: "Proposals",
          keywords: ["changeset", "review", "submit"],
          when: (ctx) => ctx.activeRepo != null,
          run: () => setProposeOpen(true),
        },
      ],
      contextMenu: [
        {
          id: "history.ctx.comment",
          target: "node",
          when: (ctx) => ctx.activeRepo != null,
          items: (_ctx, moduleId) => [
            { label: "Comment here", run: () => startNodeComment(moduleId) },
          ],
        },
      ],
    },
  };
}
